/** @format */

import { getFontEmbedCSS, toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import { TRect } from "./layout";
import { hexToRgb } from "./people";

export type TExportArea = "all" | "view";
export type TExportFormat = "pdf" | "png";
export type TExportPage = "a4" | "a3";
export type TExportOrientation = "auto" | "landscape" | "portrait";

export interface TExportOptions {
  area: TExportArea;
  format: TExportFormat;
  page: TExportPage;
  orientation: TExportOrientation;
  withLegend: boolean;
}

export interface TLegendStructure {
  name: string;
  background_color: string;
  text_color: string;
}

interface TExportParams {
  /** elemento .react-flow__viewport */
  viewportEl: HTMLElement;
  /** dimensioni del riquadro visibile del grafico */
  containerSize: { width: number; height: number };
  /** trasformazione corrente del grafico */
  viewport: { x: number; y: number; zoom: number };
  /** ingombro di tutto l'organigramma (coordinate del grafico) */
  bounds: TRect;
  options: TExportOptions;
  title: string;
  subtitle?: string;
  legend: TLegendStructure[];
  hasStaff: boolean;
}

const PADDING = 48;
const MAX_SIDE_PX = 9000; // limite prudente per i canvas del browser
const AVATAR_PLACEHOLDER =
  "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAIAAACQd1PeAAAADElEQVR4nGO4efMmAAUaAoy1sJipAAAAAElFTkSuQmCC";

// I font usati vengono incorporati una sola volta e riutilizzati negli export successivi
let cachedFontCSS: string | null = null;

const todayLabel = (): string =>
  new Date().toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit", year: "numeric" });

const fileStamp = (): string => new Date().toISOString().slice(0, 10);

const excludeFromExport = (node: HTMLElement): boolean =>
  !(node.classList && node.classList.contains("org-no-export"));

/** Cattura il grafico come PNG (data URL). */
const captureChart = async (
  params: TExportParams,
): Promise<{ dataUrl: string; width: number; height: number }> => {
  const { viewportEl, containerSize, viewport, bounds, options } = params;

  let width: number;
  let height: number;
  let transform: string;

  if (options.area === "all") {
    width = Math.ceil(bounds.width + PADDING * 2);
    height = Math.ceil(bounds.height + PADDING * 2);
    transform = `translate(${PADDING - bounds.x}px, ${PADDING - bounds.y}px) scale(1)`;
  } else {
    width = Math.ceil(containerSize.width);
    height = Math.ceil(containerSize.height);
    transform = `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`;
  }

  const pixelRatio = Math.max(0.5, Math.min(2, MAX_SIDE_PX / Math.max(width, height)));

  if (cachedFontCSS === null) {
    try {
      cachedFontCSS = await getFontEmbedCSS(viewportEl);
    } catch {
      cachedFontCSS = "";
    }
  }

  const dataUrl = await toPng(viewportEl, {
    backgroundColor: "#ffffff",
    width,
    height,
    pixelRatio,
    style: { width: `${width}px`, height: `${height}px`, transform },
    filter: excludeFromExport,
    imagePlaceholder: AVATAR_PLACEHOLDER,
    fontEmbedCSS: cachedFontCSS || undefined,
  });

  return { dataUrl, width, height };
};

const downloadDataUrl = (dataUrl: string, filename: string): void => {
  const link = document.createElement("a");
  link.href = dataUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

/** Legenda in fondo alla pagina: tipi di collegamento + strutture presenti. */
const drawLegend = (
  pdf: jsPDF,
  items: TLegendStructure[],
  hasStaff: boolean,
  left: number,
  bottom: number,
  maxWidth: number,
  measureOnly: boolean,
): number => {
  const rowHeight = 6;
  const gap = 6;
  pdf.setFont("helvetica", "normal");
  pdf.setFontSize(8);

  type TEntry = { width: number; draw: (x: number, y: number) => void };
  const entries: TEntry[] = [];

  const lineEntry = (label: string, dashed: boolean, color: [number, number, number]): TEntry => ({
    width: 9 + pdf.getTextWidth(label),
    draw: (x, y): void => {
      pdf.setDrawColor(color[0], color[1], color[2]);
      pdf.setLineWidth(0.5);
      if (dashed) pdf.setLineDashPattern([1.2, 1], 0);
      pdf.line(x, y - 1, x + 6, y - 1);
      pdf.setLineDashPattern([], 0);
      pdf.setTextColor(90, 90, 90);
      pdf.text(label, x + 8, y);
    },
  });

  entries.push(lineEntry("Linea gerarchica", false, [160, 170, 184]));
  if (hasStaff) entries.push(lineEntry("Staff", true, [217, 164, 65]));

  items.forEach(item => {
    entries.push({
      width: 7 + pdf.getTextWidth(item.name),
      draw: (x, y): void => {
        const [br, bg, bb] = hexToRgb(item.background_color);
        const [tr, tg, tb] = hexToRgb(item.text_color);
        pdf.setFillColor(br, bg, bb);
        pdf.setDrawColor(tr, tg, tb);
        pdf.setLineWidth(0.3);
        pdf.roundedRect(x, y - 3, 5, 3.6, 0.8, 0.8, "FD");
        pdf.setTextColor(60, 60, 60);
        pdf.text(item.name, x + 7, y);
      },
    });
  });

  // Disposizione su più righe
  const rows: TEntry[][] = [[]];
  let rowWidth = 0;
  entries.forEach(entry => {
    if (rowWidth > 0 && rowWidth + entry.width > maxWidth) {
      rows.push([]);
      rowWidth = 0;
    }
    rows[rows.length - 1].push(entry);
    rowWidth += entry.width + gap;
  });

  const height = rows.length * rowHeight;
  if (measureOnly) return height;

  rows.forEach((row, i) => {
    let x = left;
    const y = bottom - height + (i + 1) * rowHeight - 1.5;
    row.forEach(entry => {
      entry.draw(x, y);
      x += entry.width + gap;
    });
  });
  return height;
};

const buildPdf = (
  image: { dataUrl: string; width: number; height: number },
  params: TExportParams,
): jsPDF => {
  const { options, title, subtitle, legend, hasStaff } = params;
  const orientation =
    options.orientation === "auto"
      ? image.width >= image.height
        ? "landscape"
        : "portrait"
      : options.orientation;

  const pdf = new jsPDF({ orientation, unit: "mm", format: options.page, compress: true });
  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 10;

  let top = margin;
  let bottom = pageHeight - margin;

  if (options.withLegend) {
    // Intestazione
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(15);
    pdf.setTextColor(30, 30, 30);
    pdf.text(title, margin, margin + 5);

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(9);
    pdf.setTextColor(120, 120, 120);
    if (subtitle) pdf.text(subtitle, margin, margin + 10.5);
    pdf.text(`Aggiornato al ${todayLabel()}`, pageWidth - margin, margin + 5, { align: "right" });

    pdf.setDrawColor(225, 225, 225);
    pdf.setLineWidth(0.3);
    pdf.line(margin, margin + 14, pageWidth - margin, margin + 14);
    top = margin + 18;

    // Legenda
    const legendHeight = drawLegend(pdf, legend, hasStaff, margin, bottom, pageWidth - margin * 2, true);
    drawLegend(pdf, legend, hasStaff, margin, bottom, pageWidth - margin * 2, false);
    bottom -= legendHeight + 4;
  }

  const areaWidth = pageWidth - margin * 2;
  const areaHeight = bottom - top;
  const ratio = Math.min(areaWidth / image.width, areaHeight / image.height);
  const printWidth = image.width * ratio;
  const printHeight = image.height * ratio;

  pdf.addImage(
    image.dataUrl,
    "PNG",
    margin + (areaWidth - printWidth) / 2,
    top + (areaHeight - printHeight) / 2,
    printWidth,
    printHeight,
    undefined,
    "FAST",
  );

  return pdf;
};

export const exportOrganigramma = async (params: TExportParams): Promise<void> => {
  const image = await captureChart(params);
  const suffix = params.options.area === "view" ? "_vista" : "";
  const filename = `organigramma${suffix}_${fileStamp()}`;

  if (params.options.format === "png") {
    downloadDataUrl(image.dataUrl, `${filename}.png`);
    return;
  }

  buildPdf(image, params).save(`${filename}.pdf`);
};
